import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface ProgressBarSerieProps {
  serie: ProgressSerie;
  index: number;
  radius: number;
  total: number;
  hideEmptyValues: boolean;
  rightAlignLastItem: boolean;
  onClick?: (index: number, serie: ProgressSerie) => void;
  onMouseOver?: (index: number, serie: ProgressSerie) => void;
  onMouseOut?: (index: number, serie: ProgressSerie) => void;
  selectedSerieIndex?: number;
  minWidth?: string;
}

export interface ProgressSerie {
  value: number;
  color: string;
  extra?: any;
  hoverColor?: string;
  labelRenderer?: RendererOf<number>;
  legendRenderer?: (val: number, extra?: any) => ReactNode;
  tooltipRenderer?: (children: ReactNode) => ReactNode;
}

/*
 * Styles.
 */

const StyledProgressBarSerieWrapperDiv = styled.div<{
  $minWidth?: string;
  $width: number;
  $color: string;
  $hoverColor?: string;
  $radius: number;
  $hideEmptyValues: boolean;
  $rightAlignLastItem: boolean;
  $hasRenderer: boolean;
  $hasClickEvent: boolean;
  $isSelected?: boolean;
}>`
  padding: 0 ${(p) => ((p.$hideEmptyValues && !p.$width) || !p.$hasRenderer ? '0' : '12px')};

  width: ${(p) => p.$width}%;
  ${(p) => (p.$minWidth && p.$width > 0 ? `min-width: ${p.$minWidth};` : '')}

  background-color: ${(p) => p.$color};

  &:last-of-type {
    justify-content: ${(p) => (p.$rightAlignLastItem ? 'flex-end' : 'flex-start')};
    border-top-right-radius: ${(p) => p.$radius}px;
    border-bottom-right-radius: ${(p) => p.$radius}px;
  }

  ${(p) =>
    p.$isSelected &&
    css`
      background-color: ${p.$hoverColor || p.$color};
    `}

  ${(p) =>
    p.$hasClickEvent &&
    css`
      &:hover {
        background-color: ${p.$hoverColor || p.$color};
      }
    `}

    // Make children, including the tooltip, fill the space.
  > * {
    ${flexCenter}
    height: 100%;
  }
`;

/*
 * Component.
 */

export const ProgressBarSerie: FC<ProgressBarSerieProps> = ({
  index,
  serie,
  radius,
  total,
  hideEmptyValues,
  rightAlignLastItem,
  onClick,
  onMouseOver,
  onMouseOut,
  selectedSerieIndex,
  minWidth,
}) => (
  <StyledProgressBarSerieWrapperDiv
    $minWidth={minWidth}
    $width={(serie.value * 100) / total}
    $color={serie.color}
    $hoverColor={serie.hoverColor}
    $radius={radius}
    $hideEmptyValues={hideEmptyValues}
    $hasRenderer={Boolean(serie.labelRenderer)}
    $hasClickEvent={Boolean(onClick)}
    $isSelected={selectedSerieIndex === index}
    $rightAlignLastItem={rightAlignLastItem}
    onClick={() => onClick?.(index, serie)}
    onMouseOver={() => onMouseOver?.(index, serie)}
    onMouseOut={() => onMouseOut?.(index, serie)}
  >
    {maybeRenderAndWrapWithTooltip(serie, hideEmptyValues)}
  </StyledProgressBarSerieWrapperDiv>
);

/*
 * Helpers.
 */

function maybeRenderAndWrapWithTooltip(
  {value, labelRenderer, tooltipRenderer}: ProgressSerie,
  hideEmptyValues: boolean,
) {
  const valueToRender = maybeRenderValue(value, hideEmptyValues, labelRenderer);

  if (tooltipRenderer) {
    return tooltipRenderer(valueToRender);
  }

  return valueToRender;
}

function maybeRenderValue(value: number, hideEmptyValues: boolean, labelRenderer?: RendererOf<number>) {
  if (!labelRenderer || (hideEmptyValues && !value)) {
    return null;
  }

  return labelRenderer(value);
}
